import { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import RescheduleModal from "./RescheduleModal";

import { logoutUser } from "../../redux/slices/authSlice";
import { updatedataanasave } from "../../redux/apis/Businessuser/updateDatasaveDb";

import {
  FaUser,
  FaPhone,
  FaUserTag,
  FaCalendarAlt,
  FaEnvelope,
  FaClock,
  FaMapMarkerAlt,
  FaFlask,
  FaFileMedical,
  FaCheckCircle,
  FaTimesCircle,
  FaHourglassHalf,
  FaStethoscope,
  FaGraduationCap,
  FaIdCard,
  FaExternalLinkAlt,
} from "react-icons/fa";

import { ChevronDownIcon } from "@heroicons/react/24/solid";

import { cancelAppointment } from "../../redux/slices/appointmentsSlice";
import cancelAppointmentFromDb from "../../redux/apis/cancelAppointment";
import { deleteUserAccount } from "../../redux/apis/normalUser/handleDeleteAccount";
import { fetchAppointmentsByPatient } from "../../redux/apis/normalUser/fetchAppointmentsByPatient";

import Notification from "./Notification";

const UserDashboard = () => {
  const {
    user,
    loading: authLoading,
    isAuthenticated,
    userId,
  } = useSelector((state) => state.auth);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  // ============================================================
  // CONFIG
  // ============================================================

  const BASE_URL =
    process.env.REACT_APP_BACKEND_BASE_URL || "http://localhost:5000";

  // ============================================================
  // STATE
  // ============================================================

  const [appointments, setAppointments] = useState([]);
  const [loadingAppointments, setLoadingAppointments] = useState(true);

  const [imageError, setImageError] = useState({});
  const [patientImageError, setPatientImageError] = useState(false);

  const [actionLoading, setActionLoading] = useState(null);

  const [isRescheduleModalOpen, setIsRescheduleModalOpen] = useState(false);

  const [selectedAppointment, setSelectedAppointment] = useState(null);

  const [newDate, setNewDate] = useState("");
  const [newTime, setNewTime] = useState("");

  const [isOpen, setIsOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);

  const [expandedAppointment, setExpandedAppointment] = useState(null);

  // ============================================================
  // NOTIFICATIONS
  // ============================================================

  const notifications = useSelector((state) => state.notification?.items || []);

  // ============================================================
  // SAFE IMAGE URL
  // ============================================================

  const buildImageUrl = (image) => {
    if (!image || typeof image !== "string") {
      return "";
    }

    const trimmed = image.trim();

    if (!trimmed) {
      return "";
    }

    if (
      trimmed.startsWith("http://") ||
      trimmed.startsWith("https://") ||
      trimmed.startsWith("blob:")
    ) {
      return trimmed;
    }

    let cleanPath = trimmed
      .replace("http://localhost:5000", "")
      .replace("https://localhost:5000", "")
      .replace(/\\/g, "/");

    if (!cleanPath.startsWith("/")) {
      cleanPath = `/${cleanPath}`;
    }

    return `${BASE_URL}${cleanPath}`;
  };

  // ============================================================
  // PATIENT IMAGE
  // ============================================================

  const patientImageUrl = buildImageUrl(
    user?.profilePic ||
      user?.profilePicUrl ||
      user?.profileImage ||
      user?.image,
  );

  // ============================================================
  // FETCH APPOINTMENTS
  // ============================================================

  useEffect(() => {
    if (!isAuthenticated || !userId) {
      setLoadingAppointments(false);
      return;
    }

    setLoadingAppointments(true);

    dispatch(fetchAppointmentsByPatient(userId))
      .unwrap()
      .then((data) => {
        setAppointments(Array.isArray(data) ? data : []);
      })
      .catch((error) => {
        console.error("Fetch appointments failed:", error);

        setAppointments([]);
      })
      .finally(() => {
        setLoadingAppointments(false);
      });
  }, [dispatch, isAuthenticated, userId]);

  // ============================================================
  // TOGGLE APPOINTMENT
  // ============================================================

  const toggleAppointment = (appointmentId) => {
    if (!appointmentId) {
      return;
    }

    setExpandedAppointment((previous) =>
      previous === appointmentId ? null : appointmentId,
    );
  };

  // ============================================================
  // GROUP APPOINTMENTS BY DOCTOR
  // ============================================================

  const groupedAppointments = useMemo(() => {
    if (!Array.isArray(appointments)) {
      return {};
    }

    return appointments.reduce((acc, appt) => {
      const doctor = appt?.doctorInfo || {};

      const doctorId =
        doctor?._id ||
        doctor?.id ||
        doctor?.email ||
        doctor?.phone ||
        appt?.doctorId ||
        appt?.appointmentId ||
        appt?._id;

      if (!doctorId) {
        return acc;
      }

      if (!acc[doctorId]) {
        acc[doctorId] = {
          doctorInfo: doctor,
          appointments: [],
        };
      }

      acc[doctorId].appointments.push(appt);

      return acc;
    }, {});
  }, [appointments]);

  // ============================================================
  // DATE KEY
  // ============================================================

  const getDateKey = (date) => {
    if (!date) {
      return "unknown";
    }

    const parsed = new Date(date);

    if (Number.isNaN(parsed.getTime())) {
      return String(date).split("T")[0];
    }

    const year = parsed.getFullYear();

    const month = String(parsed.getMonth() + 1).padStart(2, "0");

    const day = String(parsed.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  // ============================================================
  // GROUP APPOINTMENTS BY DATE
  // ============================================================

  const groupAppointmentsByDate = (list) => {
    if (!Array.isArray(list)) {
      return {};
    }

    return list.reduce((acc, appt) => {
      const key = getDateKey(appt?.date);

      if (!acc[key]) {
        acc[key] = [];
      }

      acc[key].push(appt);

      return acc;
    }, {});
  };

  // ============================================================
  // DOCTOR IMAGE
  // ============================================================

  const getDoctorImage = (doctor) => {
    if (!doctor) {
      return "";
    }

    const image =
      doctor?.profilePic ||
      doctor?.profilePicUrl ||
      doctor?.profileImage ||
      doctor?.image ||
      doctor?.photo ||
      doctor?.avatar;

    return buildImageUrl(image);
  };

  // ============================================================
  // STATUS CONFIG
  // ============================================================

  const getStatusConfig = (status) => {
    const normalized = String(status || "pending")
      .toLowerCase()
      .trim();

    switch (normalized) {
      case "confirmed":
        return {
          label: "Confirmed",
          className: "bg-blue-50 text-blue-700 border-blue-200",
          icon: <FaCheckCircle />,
        };

      case "completed":
        return {
          label: "Completed",
          className: "bg-green-50 text-green-700 border-green-200",
          icon: <FaCheckCircle />,
        };

      case "declined":
      case "rejected":
        return {
          label: "Declined",
          className: "bg-red-50 text-red-700 border-red-200",
          icon: <FaTimesCircle />,
        };

      case "cancelled":
      case "canceled":
        return {
          label: "Cancelled",
          className: "bg-gray-100 text-gray-600 border-gray-200",
          icon: <FaTimesCircle />,
        };

      case "in-progress":
      case "in progress":
        return {
          label: "In Progress",
          className: "bg-purple-50 text-purple-700 border-purple-200",
          icon: <FaHourglassHalf />,
        };

      default:
        return {
          label: "Pending",
          className: "bg-yellow-50 text-yellow-700 border-yellow-200",
          icon: <FaHourglassHalf />,
        };
    }
  };

  // ============================================================
  // FORMAT DATE
  // ============================================================

  const formatDate = (date) => {
    if (!date) {
      return "N/A";
    }

    const parsed = new Date(date);

    if (Number.isNaN(parsed.getTime())) {
      return String(date).split("T")[0];
    }

    return parsed.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // ============================================================
  // FORMAT DATE HEADING
  // ============================================================

  const formatDateHeading = (date) => {
    if (!date || date === "unknown") {
      return "Date Not Available";
    }

    const parsed = new Date(`${date}T00:00:00`);

    if (Number.isNaN(parsed.getTime())) {
      return date;
    }

    return parsed.toLocaleDateString("en-IN", {
      weekday: "long",
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  // ============================================================
  // REPORT URL
  // ============================================================

  const getFileUrl = (file) => {
    const rawUrl = file?.fileUrl || file?.url || file?.path || "";

    return buildImageUrl(rawUrl);
  };

  // ============================================================
  // CANCEL APPOINTMENT
  // ============================================================

  const handleCancel = async (appointmentId) => {
    if (!appointmentId) {
      return;
    }

    if (!window.confirm("Are you sure you want to cancel this appointment?")) {
      return;
    }

    setActionLoading(appointmentId);

    try {
      const result = await dispatch(cancelAppointmentFromDb(appointmentId));

      if (cancelAppointmentFromDb.fulfilled.match(result)) {
        dispatch(cancelAppointment(appointmentId));

        setAppointments((prev) =>
          prev.filter(
            (data) =>
              data.appointmentId !== appointmentId &&
              data._id !== appointmentId,
          ),
        );

        setExpandedAppointment(null);
      } else {
        alert(
          result.payload || "Failed to cancel appointment. Please try again.",
        );
      }
    } catch (error) {
      console.error("Cancel appointment error:", error);

      alert("Failed to cancel appointment.");
    } finally {
      setActionLoading(null);
    }
  };

  // ============================================================
  // RESCHEDULE
  // ============================================================

  const handleReschedule = (appointment) => {
    if (!appointment) {
      return;
    }

    setSelectedAppointment(appointment);

    setNewDate(appointment?.date ? String(appointment.date).slice(0, 10) : "");

    setNewTime(appointment?.time || "");

    setIsRescheduleModalOpen(true);
  };

  const handleRescheduleSubmit = async (date, time) => {
    if (!selectedAppointment?.appointmentId) {
      return;
    }

    const appointmentId = selectedAppointment.appointmentId;

    setActionLoading(appointmentId);

    try {
      const response = await dispatch(
        updatedataanasave({
          appointmentId,
          date,
          time,
        }),
      );

      if (response?.meta && response.meta.requestStatus === "rejected") {
        throw new Error(response.payload || "Failed to update appointment");
      }

      setAppointments((prev) =>
        prev.map((appt) =>
          appt.appointmentId === appointmentId
            ? {
                ...appt,
                previousDate: appt.date,
                previousTime: appt.time,
                date,
                time,
              }
            : appt,
        ),
      );

      setIsRescheduleModalOpen(false);
      setSelectedAppointment(null);
    } catch (error) {
      console.error("Failed to reschedule appointment:", error);

      alert(error?.message || "Rescheduling failed. Please try again.");
    } finally {
      setActionLoading(null);
    }
  };

  // ============================================================
  // PROFILE ACTIONS
  // ============================================================

  const handleProfileEdit = () => {
    setIsOpen(false);
    navigate("/edit-profile");
  };

  const handleChangePassword = () => {
    setIsOpen(false);
    navigate("/change-password");
  };

  const handleDeleteAccount = async () => {
    if (
      !window.confirm(
        "Are you sure you want to permanently delete your account?",
      )
    ) {
      return;
    }

    setActionLoading("delete");

    try {
      await dispatch(deleteUserAccount(user._id)).unwrap();

      dispatch(logoutUser());

      alert("Account deleted successfully");

      navigate("/login", {
        replace: true,
      });
    } catch (error) {
      console.error("Delete account error:", error);

      alert(`Error deleting account: ${error?.message || error}`);
    } finally {
      setActionLoading(null);
    }
  };

  // ============================================================
  // ALL HOOKS ARE ABOVE THIS POINT
  // ============================================================

  if (authLoading || loadingAppointments) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="bg-white px-8 py-6 rounded-2xl shadow-lg text-center">
          <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4" />

          <p className="text-gray-700 font-semibold">
            Loading your dashboard...
          </p>
        </div>
      </div>
    );
  }

  // ============================================================
  // USER NOT FOUND
  // ============================================================

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="bg-white p-8 rounded-2xl shadow-lg text-center">
          <FaUser className="text-gray-400 text-4xl mx-auto mb-3" />

          <p className="text-gray-700 font-semibold">User not found</p>
        </div>
      </div>
    );
  }

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/40 to-cyan-50/30">
      {/* ======================================================
          PROFILE HEADER
      ====================================================== */}

      <div className="px-4 sm:px-6 lg:px-8 pt-5">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-visible">
            {/* TOP DARK HEADER */}

            <div
              className="h-20 sm:h-24 rounded-t-2xl"
              style={{
                backgroundImage:
                  "linear-gradient(145deg, rgb(15,31,51) 0%, rgb(26,58,92) 50%, rgb(30,73,118) 100%)",
              }}
            />

            <div className="px-4 sm:px-6 pb-5">
              <div className="-mt-8 sm:-mt-10 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
                {/* PATIENT */}

                <div className="flex flex-col sm:flex-row sm:items-end gap-3 min-w-0">
                  {/* PATIENT IMAGE */}

                  <div className="relative z-10 flex-shrink-0">
                    {patientImageUrl && !patientImageError ? (
                      <img
                        src={patientImageUrl}
                        alt={user?.fullName || user?.name || "Patient"}
                        className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl object-cover border-4 border-white shadow-lg bg-white"
                        onError={() => setPatientImageError(true)}
                      />
                    ) : (
                      <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl bg-white border-4 border-white shadow-lg flex items-center justify-center">
                        <div
                          className="w-full h-full rounded-lg flex items-center justify-center"
                          style={{
                            background:
                              "linear-gradient(135deg, rgb(239,246,255), rgb(224,242,254))",
                          }}
                        >
                          <FaUser className="text-[#1a3a5c] text-3xl" />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* PATIENT DETAILS */}

                  <div className="pb-0.5 min-w-0">
                    <h1 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">
                      {user?.fullName || user?.name || "Patient"}
                    </h1>

                    <p className="text-sm text-gray-500 mt-0.5 flex items-center gap-2">
                      <FaEnvelope className="text-sky-500 flex-shrink-0" />

                      <span className="truncate">
                        {user?.email || "Email not available"}
                      </span>
                    </p>

                    {user?.phone && (
                      <p className="text-sm text-gray-500 mt-0.5 flex items-center gap-2">
                        <FaPhone className="text-green-500 flex-shrink-0" />

                        {user.phone}
                      </p>
                    )}
                  </div>
                </div>

                {/* HEADER ACTIONS */}

                <div className="relative z-50 flex flex-wrap items-center gap-2">
                  {/* NOTIFICATIONS */}

                  <button
                    type="button"
                    onClick={() => setShowNotifications(true)}
                    className="inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md whitespace-nowrap"
                    style={{
                      backgroundImage:
                        "linear-gradient(135deg, rgb(26,58,92), rgb(14,165,233))",
                    }}
                  >
                    <span>🔔</span>

                    <span className="hidden sm:inline">Notifications</span>

                    {notifications.length > 0 && (
                      <span className="flex min-w-5 h-5 items-center justify-center rounded-full bg-white px-1 text-xs font-bold text-[#1a3a5c]">
                        {notifications.length}
                      </span>
                    )}
                  </button>

                  {/* ACCOUNT */}

                  <div className="relative z-[100]">
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();

                        setIsOpen((prev) => !prev);
                      }}
                      className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-bold text-[#1a3a5c] shadow-sm transition-all hover:-translate-y-0.5 hover:border-sky-300 hover:bg-sky-50 hover:shadow-md whitespace-nowrap"
                    >
                      <FaUser />
                      Account
                      <ChevronDownIcon
                        className={`h-4 w-4 transition-transform duration-200 ${
                          isOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>

                    {isOpen && (
                      <div className="absolute right-0 top-full z-[9999] mt-2 w-56 overflow-hidden rounded-2xl border border-gray-200 bg-white p-1.5 shadow-[0_15px_40px_rgba(7,29,48,0.18)]">
                        <button
                          type="button"
                          onClick={handleProfileEdit}
                          className="flex w-full items-center rounded-xl px-3 py-2.5 text-left text-sm font-semibold text-gray-700 transition hover:bg-sky-50 hover:text-[#1a3a5c]"
                        >
                          <FaUser className="mr-2.5 text-sky-500" />
                          Edit Profile
                        </button>

                        <button
                          type="button"
                          onClick={handleChangePassword}
                          className="flex w-full items-center rounded-xl px-3 py-2.5 text-left text-sm font-semibold text-gray-700 transition hover:bg-sky-50 hover:text-[#1a3a5c]"
                        >
                          <FaUserTag className="mr-2.5 text-sky-500" />
                          Change Password
                        </button>

                        <button
                          type="button"
                          onClick={() => {
                            if (actionLoading !== "delete") {
                              setIsOpen(false);

                              handleDeleteAccount();
                            }
                          }}
                          disabled={actionLoading === "delete"}
                          className="flex w-full items-center rounded-xl px-3 py-2.5 text-left text-sm font-semibold text-red-600 transition hover:bg-red-50 hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <FaTimesCircle className="mr-2.5" />

                          {actionLoading === "delete"
                            ? "Deleting..."
                            : "Delete Account"}
                        </button>
                      </div>
                    )}
                  </div>

                  {/* LOGO */}

                  <button
                    type="button"
                    onClick={() => navigate("/Home")}
                    className="hidden md:flex items-center justify-center rounded-xl bg-white px-2 py-1 border border-gray-100 transition-all hover:-translate-y-0.5 hover:shadow-md hover:border-sky-200 overflow-hidden"
                    title="MR Health Home"
                    aria-label="MR Health Home"
                  >
                    <img
                      src="/logo.jpeg"
                      alt="MR Health"
                      className="h-12 w-auto max-w-[150px] object-contain sm:h-14 md:h-16 lg:h-[68px]"
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ======================================================
          NOTIFICATIONS
      ====================================================== */}

      {showNotifications && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden relative">
            <button
              type="button"
              onClick={() => setShowNotifications(false)}
              className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition"
            >
              ✕
            </button>

            <div className="p-4 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-800">Notifications</h2>
            </div>

            <div className="max-h-[75vh] overflow-y-auto p-3">
              <Notification
                currentUser={user}
                onClose={() => setShowNotifications(false)}
              />
            </div>
          </div>
        </div>
      )}

      {/* ======================================================
          APPOINTMENTS
      ====================================================== */}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* TITLE */}

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-[#0f1f33]">
              My Appointments
            </h2>

            <p className="text-sm text-gray-500 mt-0.5">
              View your appointments, prescriptions and reports.
            </p>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl px-4 py-2 shadow-sm">
            <span className="text-xs text-gray-500">Total</span>

            <span className="ml-2 text-base font-bold text-[#1a3a5c]">
              {appointments.length}
            </span>
          </div>
        </div>

        {/* ====================================================
            NO APPOINTMENTS
        ==================================================== */}

        {Object.keys(groupedAppointments).length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-10 text-center">
            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
              style={{
                background:
                  "linear-gradient(135deg, rgb(239,246,255), rgb(224,242,254))",
              }}
            >
              <FaCalendarAlt className="text-[#1a3a5c] text-2xl" />
            </div>

            <h3 className="text-lg font-bold text-[#0f1f33]">
              No appointments yet
            </h3>

            <p className="text-sm text-gray-500 mt-1">
              You have no appointments booked.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedAppointments)
              .reverse()
              .map(([doctorId, group]) => {
                const doctor = group?.doctorInfo || {};

                const doctorImage = getDoctorImage(doctor);

                const doctorImageFailed = imageError[doctorId];

                // =================================================
                // DATE-WISE GROUPING
                // =================================================

                const dateGroups = groupAppointmentsByDate(
                  group?.appointments || [],
                );

                const sortedDates = Object.keys(dateGroups).sort(
                  (a, b) => new Date(a) - new Date(b),
                );

                // =================================================
                // DOCTOR LOCATION
                // =================================================

                const doctorAddress =
                  doctor?.address ||
                  doctor?.clinicAddress ||
                  doctor?.hospitalAddress ||
                  "";

                const doctorArea =
                  doctor?.area ||
                  doctor?.locality ||
                  doctor?.locationArea ||
                  "";

                const doctorCity = doctor?.city || doctor?.town || "";

                const doctorState = doctor?.state || doctor?.stateName || "";

                const doctorPincode =
                  doctor?.pincode ||
                  doctor?.pinCode ||
                  doctor?.postalCode ||
                  doctor?.zipCode ||
                  "";

                const doctorMapLink =
                  doctor?.googleMapLink ||
                  doctor?.googleMapsLink ||
                  doctor?.mapLink ||
                  doctor?.locationUrl ||
                  doctor?.googleMapsUrl ||
                  "";

                const doctorSpecialization =
                  doctor?.specialization ||
                  doctor?.speciality ||
                  doctor?.specialty ||
                  "";

                const doctorQualification =
                  doctor?.qualification ||
                  doctor?.qualifications ||
                  doctor?.degree ||
                  "";

                const doctorRegistration =
                  doctor?.registrationNumber ||
                  doctor?.registrationNo ||
                  doctor?.medicalRegistrationNumber ||
                  doctor?.registrationId ||
                  "";

                const hasDoctorLocation =
                  doctorAddress ||
                  doctorArea ||
                  doctorCity ||
                  doctorState ||
                  doctorPincode;

                return (
                  <section
                    key={doctorId}
                    className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden"
                  >
                    {/* =================================================
                        DOCTOR HEADER
                    ================================================= */}

                    <div
                      className="px-4 sm:px-5 py-4 border-b border-[#dbe7f2]"
                      style={{
                        background:
                          "linear-gradient(145deg, rgb(239,246,255), rgb(240,249,255))",
                      }}
                    >
                      <div className="flex items-start justify-between gap-3">
                        {/* DOCTOR PROFILE */}

                        <div className="flex items-start gap-3 min-w-0 flex-1">
                          {/* IMAGE */}

                          {doctorImage && !doctorImageFailed ? (
                            <img
                              src={doctorImage}
                              alt={doctor?.name || doctor?.fullName || "Doctor"}
                              className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl object-cover border-2 border-white shadow-sm bg-white flex-shrink-0"
                              onError={() =>
                                setImageError((prev) => ({
                                  ...prev,
                                  [doctorId]: true,
                                }))
                              }
                            />
                          ) : (
                            <div
                              className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl border-2 border-white shadow-sm flex items-center justify-center flex-shrink-0"
                              style={{
                                background:
                                  "linear-gradient(135deg, rgb(219,234,254), rgb(224,242,254))",
                              }}
                            >
                              <FaStethoscope className="text-[#1a3a5c] text-xl" />
                            </div>
                          )}

                          {/* DOCTOR DETAILS */}

                          <div className="min-w-0 flex-1">
                            {/* NAME */}

                            <div className="flex flex-wrap items-center gap-2">
                              <h3 className="text-sm sm:text-base font-bold text-[#0f1f33] truncate">
                                {doctor?.name ||
                                  doctor?.fullName ||
                                  "Unknown Doctor"}
                              </h3>

                              <span className="inline-flex px-2 py-0.5 rounded-full bg-[#1a3a5c] text-white text-[9px] font-bold tracking-wide">
                                DOCTOR
                              </span>
                            </div>

                            {/* PROFESSION */}

                            <p className="text-xs text-gray-500 flex items-center gap-1.5 mt-1">
                              <FaUserTag className="text-[#0ea5e9] flex-shrink-0" />

                              <span>
                                {doctor?.profession || "Medical Professional"}
                              </span>
                            </p>

                            {/* SPECIALIZATION */}

                            {doctorSpecialization && (
                              <p className="text-[10px] sm:text-xs text-gray-600 flex items-center gap-1.5 mt-1">
                                <FaStethoscope className="text-purple-500 flex-shrink-0" />

                                <span>
                                  <strong>Specialization:</strong>{" "}
                                  {doctorSpecialization}
                                </span>
                              </p>
                            )}

                            {/* EMAIL + PHONE */}

                            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-x-4 gap-y-1 mt-1">
                              {doctor?.email && (
                                <p className="text-[10px] sm:text-xs text-gray-500 flex items-center gap-1.5 min-w-0">
                                  <FaEnvelope className="text-blue-500 flex-shrink-0" />

                                  <span className="truncate">
                                    {doctor.email}
                                  </span>
                                </p>
                              )}

                              {doctor?.phone && (
                                <p className="text-[10px] sm:text-xs text-gray-500 flex items-center gap-1.5">
                                  <FaPhone className="text-green-500 flex-shrink-0" />

                                  <span>{doctor.phone}</span>
                                </p>
                              )}
                            </div>

                            {/* LOCATION */}

                            {/* ============================================================
    DOCTOR LOCATION
============================================================ */}

                            <div className="mt-1.5">
                              {/* DEBUG
                              {console.log("🔎 RENDERING DOCTOR LOCATION:", {
                                doctorId,
                                doctor,
                                doctorAddress,
                                doctorArea,
                                doctorCity,
                                doctorState,
                                doctorPincode,
                                doctorMapLink,
                                hasDoctorLocation,
                              })} */}

                              {hasDoctorLocation ? (
                                <div className="flex items-start gap-1.5">
                                  <FaMapMarkerAlt className="text-red-500 flex-shrink-0 mt-0.5" />

                                  <div className="min-w-0">
                                    <p className="text-[10px] sm:text-xs text-gray-600 leading-4">
                                      {doctorAddress && (
                                        <span className="block font-medium text-gray-700">
                                          {doctorAddress}
                                        </span>
                                      )}

                                      {(doctorArea ||
                                        doctorCity ||
                                        doctorState ||
                                        doctorPincode) && (
                                        <span className="block">
                                          {doctorArea && (
                                            <span>{doctorArea}</span>
                                          )}

                                          {doctorArea && doctorCity && (
                                            <span>, </span>
                                          )}

                                          {doctorCity && (
                                            <span>{doctorCity}</span>
                                          )}

                                          {doctorCity && doctorState && (
                                            <span>, </span>
                                          )}

                                          {doctorState && (
                                            <span>{doctorState}</span>
                                          )}

                                          {doctorPincode && (
                                            <span>
                                              {" - "}
                                              {doctorPincode}
                                            </span>
                                          )}
                                        </span>
                                      )}
                                    </p>

                                    {/* GOOGLE MAP */}
                                    {doctorMapLink && (
                                      <a
                                        href={doctorMapLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(event) =>
                                          event.stopPropagation()
                                        }
                                        className="inline-flex items-center gap-1.5 mt-1.5 text-[10px] sm:text-xs font-bold text-[#0ea5e9] hover:text-[#1a3a5c] hover:underline"
                                      >
                                        <FaMapMarkerAlt />
                                        View Doctor Location
                                        <FaExternalLinkAlt className="text-[8px]" />
                                      </a>
                                    )}
                                  </div>
                                </div>
                              ) : (
                                <p className="text-[10px] text-gray-400">
                                  📍 Doctor location not available
                                </p>
                              )}
                            </div>

                            {/* MAP */}

                            {doctorMapLink && (
                              <a
                                href={doctorMapLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(event) => event.stopPropagation()}
                                className="inline-flex items-center gap-1.5 mt-1.5 text-[10px] sm:text-xs font-bold text-[#0ea5e9] hover:text-[#1a3a5c] hover:underline"
                              >
                                <FaMapMarkerAlt />
                                View Doctor Location
                                <FaExternalLinkAlt className="text-[8px]" />
                              </a>
                            )}

                            {/* QUALIFICATION + REGISTRATION */}

                            {(doctorQualification || doctorRegistration) && (
                              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5">
                                {doctorQualification && (
                                  <span className="text-[10px] text-gray-500 flex items-center gap-1">
                                    <FaGraduationCap className="text-indigo-500" />

                                    <span>
                                      <strong>Qualification:</strong>{" "}
                                      {doctorQualification}
                                    </span>
                                  </span>
                                )}

                                {doctorRegistration && (
                                  <span className="text-[10px] text-gray-500 flex items-center gap-1">
                                    <FaIdCard className="text-slate-500" />

                                    <span>
                                      <strong>Reg. No:</strong>{" "}
                                      {doctorRegistration}
                                    </span>
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* VISITS */}

                        <div className="flex-shrink-0 rounded-xl px-3 py-1.5 bg-white border border-[#dbe7f2] shadow-sm text-center">
                          <p className="text-[9px] uppercase tracking-wide text-gray-400 font-bold">
                            Visits
                          </p>

                          <p className="text-sm font-bold text-[#1a3a5c]">
                            {group?.appointments?.length || 0}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* =================================================
                        DATE-WISE SECTIONS
                    ================================================= */}

                    <div className="p-3 sm:p-4 space-y-5">
                      {sortedDates.map((dateKey) => {
                        const dateAppointments = dateGroups[dateKey] || [];

                        const sortedAppointments = [...dateAppointments].sort(
                          (a, b) =>
                            new Date(a?.date || 0) - new Date(b?.date || 0),
                        );

                        return (
                          <div key={dateKey} className="space-y-2.5">
                            {/* DATE HEADER */}

                            <div className="flex items-center gap-3">
                              <div
                                className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                                style={{
                                  backgroundImage:
                                    "linear-gradient(135deg, rgb(26,58,92), rgb(14,165,233))",
                                }}
                              >
                                <FaCalendarAlt className="text-white text-sm" />
                              </div>

                              <div className="min-w-0">
                                <p className="text-[9px] uppercase tracking-wider font-bold text-gray-400">
                                  Appointment Date
                                </p>

                                <h4 className="text-sm sm:text-base font-bold text-[#1a3a5c] truncate">
                                  {formatDateHeading(dateKey)}
                                </h4>
                              </div>

                              <div className="ml-auto flex-shrink-0 px-2.5 py-1 rounded-full bg-blue-50 border border-blue-100 text-[10px] font-bold text-blue-700">
                                {dateAppointments.length}{" "}
                                {dateAppointments.length === 1
                                  ? "Appointment"
                                  : "Appointments"}
                              </div>
                            </div>

                            <div className="h-px bg-gradient-to-r from-blue-100 via-cyan-100 to-transparent" />

                            {/* APPOINTMENTS */}

                            {sortedAppointments.map((appt) => {
                              const appointmentId =
                                appt?.appointmentId || appt?._id;

                              const isExpanded =
                                expandedAppointment === appointmentId;

                              const statusConfig = getStatusConfig(
                                appt?.status,
                              );

                              const normalizedStatus = String(
                                appt?.status || "pending",
                              )
                                .toLowerCase()
                                .trim();

                              const canCancel = [
                                "pending",
                                "declined",
                              ].includes(normalizedStatus);

                              const prescriptionCount = Array.isArray(
                                appt?.prescriptions,
                              )
                                ? appt.prescriptions.length
                                : 0;

                              const reportCount = Array.isArray(
                                appt?.testRecommendations,
                              )
                                ? appt.testRecommendations.reduce(
                                    (total, test) =>
                                      total +
                                      (Array.isArray(test?.labFiles)
                                        ? test.labFiles.length
                                        : 0),
                                    0,
                                  )
                                : 0;

                              return (
                                <article
                                  key={appointmentId}
                                  className={`rounded-2xl border overflow-hidden transition-all duration-200 ${
                                    isExpanded
                                      ? "border-[#9fc6df] shadow-md"
                                      : "border-gray-200 shadow-sm hover:border-[#b8d7e8]"
                                  }`}
                                >
                                  {/* CLICKABLE BAR */}

                                  <div
                                    role="button"
                                    tabIndex={0}
                                    onClick={() =>
                                      toggleAppointment(appointmentId)
                                    }
                                    onKeyDown={(event) => {
                                      if (
                                        event.key === "Enter" ||
                                        event.key === " "
                                      ) {
                                        event.preventDefault();

                                        toggleAppointment(appointmentId);
                                      }
                                    }}
                                    className="cursor-pointer select-none bg-white hover:bg-slate-50 transition"
                                  >
                                    <div className="flex flex-col lg:flex-row lg:items-center">
                                      {/* TOKEN */}

                                      <div
                                        className="flex items-center gap-2.5 px-3 sm:px-4 py-3 lg:w-[145px] flex-shrink-0"
                                        style={{
                                          background:
                                            "linear-gradient(135deg, rgb(26,58,92), rgb(30,73,118))",
                                        }}
                                      >
                                        <div className="w-8 h-8 rounded-lg bg-white/10 border border-white/20 flex items-center justify-center">
                                          <FaCalendarAlt className="text-white text-xs" />
                                        </div>

                                        <div>
                                          <p className="text-[9px] uppercase tracking-wider text-sky-200 font-bold">
                                            Token
                                          </p>

                                          <p className="text-sm font-bold text-white">
                                            #{appt?.tokenNumber || "N/A"}
                                          </p>
                                        </div>
                                      </div>

                                      {/* DATE */}

                                      <div className="flex items-center gap-2.5 px-3 sm:px-4 py-3 lg:w-[170px] flex-shrink-0 border-b lg:border-b-0 lg:border-r border-gray-100">
                                        <FaCalendarAlt className="text-[#1a3a5c] text-sm flex-shrink-0" />

                                        <div>
                                          <p className="text-[9px] uppercase tracking-wide text-gray-400 font-bold">
                                            Date
                                          </p>

                                          {appt?.previousDate ? (
                                            <div className="flex flex-wrap items-center gap-1.5">
                                              <span className="text-xs line-through text-gray-400">
                                                {formatDate(appt.previousDate)}
                                              </span>

                                              <span className="text-xs font-bold text-emerald-600">
                                                {formatDate(appt.date)}
                                              </span>
                                            </div>
                                          ) : (
                                            <p className="text-xs font-semibold text-gray-800">
                                              {formatDate(appt?.date)}
                                            </p>
                                          )}
                                        </div>
                                      </div>

                                      {/* TIME */}

                                      <div className="flex items-center gap-2.5 px-3 sm:px-4 py-3 lg:w-[145px] flex-shrink-0 border-b lg:border-b-0 lg:border-r border-gray-100">
                                        <FaClock className="text-[#0ea5e9] text-sm flex-shrink-0" />

                                        <div>
                                          <p className="text-[9px] uppercase tracking-wide text-gray-400 font-bold">
                                            Time
                                          </p>

                                          {appt?.previousTime ? (
                                            <div className="flex items-center gap-1.5">
                                              <span className="text-xs line-through text-gray-400">
                                                {appt.previousTime}
                                              </span>

                                              <span className="text-xs font-bold text-emerald-600">
                                                {appt.time}
                                              </span>
                                            </div>
                                          ) : (
                                            <p className="text-xs font-semibold text-gray-800">
                                              {appt?.time || "N/A"}
                                            </p>
                                          )}
                                        </div>
                                      </div>

                                      {/* STATUS */}

                                      <div className="px-3 sm:px-4 py-3 flex items-center lg:border-r border-gray-100">
                                        <span
                                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[10px] font-bold whitespace-nowrap ${statusConfig.className}`}
                                        >
                                          {statusConfig.icon}

                                          {statusConfig.label}
                                        </span>
                                      </div>

                                      {/* SUMMARY */}

                                      <div className="flex-1 px-3 sm:px-4 py-3 min-w-0">
                                        <div className="flex flex-wrap items-center gap-2">
                                          <span className="inline-flex items-center gap-1.5 rounded-lg bg-purple-50 border border-purple-100 px-2.5 py-1.5 text-[10px] font-semibold text-purple-700">
                                            <FaFileMedical />

                                            {prescriptionCount > 0
                                              ? `${prescriptionCount} Prescription${
                                                  prescriptionCount > 1
                                                    ? "s"
                                                    : ""
                                                }`
                                              : "No Prescription"}
                                          </span>

                                          <span className="inline-flex items-center gap-1.5 rounded-lg bg-sky-50 border border-sky-100 px-2.5 py-1.5 text-[10px] font-semibold text-sky-700">
                                            <FaFlask />

                                            {reportCount > 0
                                              ? `${reportCount} Report${
                                                  reportCount > 1 ? "s" : ""
                                                }`
                                              : "No Reports"}
                                          </span>
                                        </div>
                                      </div>

                                      {/* OPEN/CLOSE */}

                                      <div className="flex items-center justify-between lg:justify-center gap-2 px-3 sm:px-4 py-3 bg-slate-50 border-t lg:border-t-0 lg:border-l border-gray-100 min-w-[120px]">
                                        <span className="text-[10px] font-bold text-[#1a3a5c] uppercase tracking-wide">
                                          {isExpanded
                                            ? "Hide Details"
                                            : "View Details"}
                                        </span>

                                        <div
                                          className={`w-7 h-7 rounded-lg flex items-center justify-center transition-transform duration-200 ${
                                            isExpanded ? "rotate-180" : ""
                                          }`}
                                          style={{
                                            backgroundImage:
                                              "linear-gradient(135deg, rgb(26,58,92), rgb(14,165,233))",
                                          }}
                                        >
                                          <ChevronDownIcon className="w-4 h-4 text-white" />
                                        </div>
                                      </div>
                                    </div>
                                  </div>

                                  {/* =================================================
                                          EXPANDED DETAILS
                                      ================================================= */}

                                  {isExpanded && (
                                    <div className="border-t border-[#dbe7f2] bg-slate-50/60 p-3 sm:p-4">
                                      {/* ACTION BAR */}

                                      <div className="flex flex-wrap items-center justify-between gap-3 bg-white rounded-xl border border-gray-200 px-3 py-2.5 mb-3 shadow-sm">
                                        <div className="flex items-center gap-2">
                                          <div
                                            className="w-7 h-7 rounded-lg flex items-center justify-center"
                                            style={{
                                              backgroundImage:
                                                "linear-gradient(135deg, rgb(26,58,92), rgb(14,165,233))",
                                            }}
                                          >
                                            <FaCalendarAlt className="text-white text-xs" />
                                          </div>

                                          <div>
                                            <p className="text-[9px] uppercase tracking-wide text-gray-400 font-bold">
                                              Appointment
                                            </p>

                                            <p className="text-xs font-bold text-[#1a3a5c]">
                                              #{appt?.tokenNumber || "N/A"}
                                            </p>
                                          </div>
                                        </div>

                                        <div className="flex items-center gap-2">
                                          {/* CANCEL */}

                                          <button
                                            type="button"
                                            onClick={(event) => {
                                              event.stopPropagation();

                                              handleCancel(appointmentId);
                                            }}
                                            disabled={
                                              !canCancel ||
                                              actionLoading === appointmentId
                                            }
                                            className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-[11px] font-bold transition ${
                                              canCancel
                                                ? "bg-red-50 text-red-600 border border-red-200 hover:bg-red-100"
                                                : "bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed"
                                            }`}
                                          >
                                            <FaTimesCircle />

                                            {actionLoading === appointmentId
                                              ? "Cancelling..."
                                              : "Cancel"}
                                          </button>

                                          {/* RESCHEDULE */}

                                          {canCancel && (
                                            <button
                                              type="button"
                                              onClick={(event) => {
                                                event.stopPropagation();

                                                handleReschedule(appt);
                                              }}
                                              disabled={
                                                actionLoading === appointmentId
                                              }
                                              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-[11px] font-bold text-white transition shadow-sm hover:-translate-y-0.5 hover:shadow-md disabled:opacity-50"
                                              style={{
                                                backgroundImage:
                                                  "linear-gradient(135deg, rgb(26,58,92), rgb(14,165,233))",
                                              }}
                                            >
                                              <FaCalendarAlt />
                                              Reschedule
                                            </button>
                                          )}
                                        </div>
                                      </div>

                                      {/* ============================================================
    DOCTOR LOCATION
============================================================ */}

                                      {hasDoctorLocation && (
                                        <div className="mt-3 rounded-xl border border-red-100 bg-red-50/50 p-3">
                                          <div className="flex items-start gap-2">
                                            <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                                              <FaMapMarkerAlt className="text-red-500 text-sm" />
                                            </div>

                                            <div className="min-w-0 flex-1">
                                              <p className="text-[9px] uppercase tracking-wide font-bold text-red-500">
                                                Clinic / Hospital Location
                                              </p>

                                              {doctorAddress && (
                                                <p className="text-xs font-semibold text-gray-800 mt-0.5">
                                                  {doctorAddress}
                                                </p>
                                              )}

                                              {(doctorArea ||
                                                doctorCity ||
                                                doctorState ||
                                                doctorPincode) && (
                                                <p className="text-xs text-gray-600 mt-0.5">
                                                  {doctorArea && (
                                                    <span>{doctorArea}</span>
                                                  )}

                                                  {doctorArea && doctorCity && (
                                                    <span>, </span>
                                                  )}

                                                  {doctorCity && (
                                                    <span>{doctorCity}</span>
                                                  )}

                                                  {doctorCity &&
                                                    doctorState && (
                                                      <span>, </span>
                                                    )}

                                                  {doctorState && (
                                                    <span>{doctorState}</span>
                                                  )}

                                                  {doctorPincode && (
                                                    <span>
                                                      {" "}
                                                      - {doctorPincode}
                                                    </span>
                                                  )}
                                                </p>
                                              )}

                                              {doctorMapLink && (
                                                <a
                                                  href={doctorMapLink}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  onClick={(event) =>
                                                    event.stopPropagation()
                                                  }
                                                  className="inline-flex items-center gap-1.5 mt-2 px-2.5 py-1.5 rounded-lg bg-white border border-red-200 text-red-600 text-[10px] font-bold hover:bg-red-50 transition"
                                                >
                                                  <FaMapMarkerAlt />
                                                  View Location
                                                  <FaExternalLinkAlt className="text-[8px]" />
                                                </a>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      )}

                                      {/* PRESCRIPTION + NOTES */}

                                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                                        {/* PRESCRIPTIONS */}

                                        <div className="bg-white rounded-xl border border-purple-100 overflow-hidden">
                                          <div className="flex items-center gap-2 px-3 py-2.5 bg-purple-50 border-b border-purple-100">
                                            <div className="w-7 h-7 rounded-lg bg-purple-100 flex items-center justify-center">
                                              <FaFileMedical className="text-purple-600 text-xs" />
                                            </div>

                                            <h4 className="text-xs font-bold text-gray-800">
                                              Prescriptions
                                            </h4>
                                          </div>

                                          <div className="p-3">
                                            {Array.isArray(
                                              appt?.prescriptions,
                                            ) &&
                                            appt.prescriptions.length > 0 ? (
                                              <ul className="space-y-1.5">
                                                {appt.prescriptions.map(
                                                  (presc, i) => (
                                                    <li
                                                      key={i}
                                                      className="bg-purple-50/60 rounded-lg px-3 py-2 text-xs text-gray-700 border border-purple-100"
                                                    >
                                                      {typeof presc === "string"
                                                        ? presc
                                                        : presc?.medicine ||
                                                          presc?.name ||
                                                          JSON.stringify(presc)}
                                                    </li>
                                                  ),
                                                )}
                                              </ul>
                                            ) : (
                                              <p className="text-xs text-gray-500">
                                                Waiting for doctor update.
                                              </p>
                                            )}
                                          </div>
                                        </div>

                                        {/* NOTES */}

                                        <div className="bg-white rounded-xl border border-sky-100 overflow-hidden">
                                          <div className="flex items-center gap-2 px-3 py-2.5 bg-sky-50 border-b border-sky-100">
                                            <div className="w-7 h-7 rounded-lg bg-sky-100 flex items-center justify-center">
                                              <FaStethoscope className="text-[#1a3a5c] text-xs" />
                                            </div>

                                            <h4 className="text-xs font-bold text-gray-800">
                                              Doctor Notes
                                            </h4>
                                          </div>

                                          <div className="p-3">
                                            <p className="text-xs text-gray-700 leading-5">
                                              {appt?.description ||
                                                "No description provided."}
                                            </p>
                                          </div>
                                        </div>
                                      </div>

                                      {/* =================================================
                                              TEST RECOMMENDATIONS
                                          ================================================= */}

                                      {Array.isArray(
                                        appt?.testRecommendations,
                                      ) &&
                                        appt.testRecommendations.length > 0 && (
                                          <div className="mt-3 bg-white rounded-xl border border-cyan-100 overflow-hidden">
                                            <div className="flex items-center gap-2 px-3 py-2.5 bg-cyan-50 border-b border-cyan-100">
                                              <div className="w-7 h-7 rounded-lg bg-cyan-100 flex items-center justify-center">
                                                <FaFlask className="text-cyan-700 text-xs" />
                                              </div>

                                              <div>
                                                <h4 className="text-xs font-bold text-gray-800">
                                                  Test Recommendations
                                                </h4>

                                                <p className="text-[10px] text-gray-500">
                                                  Laboratory tests recommended
                                                  by your doctor
                                                </p>
                                              </div>
                                            </div>

                                            <div className="p-3 space-y-2">
                                              {appt.testRecommendations.map(
                                                (test, idx) => {
                                                  const testStatus =
                                                    getStatusConfig(
                                                      test?.status,
                                                    );

                                                  return (
                                                    <div
                                                      key={idx}
                                                      className="rounded-xl border border-gray-200 bg-slate-50 p-3"
                                                    >
                                                      {/* TEST HEADER */}

                                                      <div className="flex flex-wrap items-center justify-between gap-2">
                                                        <div>
                                                          <p className="text-[9px] text-gray-400 uppercase tracking-wide font-bold">
                                                            Test Type
                                                          </p>

                                                          <span className="inline-flex mt-1 px-2.5 py-1 rounded-lg bg-[#1a3a5c] text-white text-[10px] font-bold">
                                                            {test?.testType ||
                                                              "N/A"}
                                                          </span>
                                                        </div>

                                                        <span
                                                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-[10px] font-bold ${testStatus.className}`}
                                                        >
                                                          {testStatus.icon}

                                                          {testStatus.label}
                                                        </span>
                                                      </div>

                                                      {/* DOCTOR DESCRIPTION */}

                                                      {test?.doctorDescription && (
                                                        <div className="mt-2.5 bg-white rounded-lg border border-gray-100 p-2.5">
                                                          <p className="text-[9px] uppercase tracking-wide font-bold text-gray-400 mb-1">
                                                            Doctor's Notes
                                                          </p>

                                                          <p className="text-xs text-gray-700 leading-5">
                                                            {
                                                              test.doctorDescription
                                                            }
                                                          </p>
                                                        </div>
                                                      )}

                                                      {/* LAB USER */}

                                                      {test?.OtherMedicalUser && (
                                                        <div className="mt-2.5 rounded-xl border border-emerald-200 bg-emerald-50 p-3">
                                                          <div className="flex items-center gap-2 mb-2.5">
                                                            <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                                                              <FaFlask className="text-emerald-600 text-xs" />
                                                            </div>

                                                            <div>
                                                              <h5 className="text-xs font-bold text-emerald-800">
                                                                Lab User
                                                              </h5>

                                                              <p className="text-[10px] text-emerald-700">
                                                                Test processing
                                                                information
                                                              </p>
                                                            </div>
                                                          </div>

                                                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-2 text-xs text-gray-700">
                                                            <p>
                                                              <strong>
                                                                Name:
                                                              </strong>{" "}
                                                              {test
                                                                .OtherMedicalUser
                                                                ?.fullName ||
                                                                "N/A"}
                                                            </p>

                                                            <p>
                                                              <strong>
                                                                Profession:
                                                              </strong>{" "}
                                                              {test
                                                                .OtherMedicalUser
                                                                ?.profession ||
                                                                "N/A"}
                                                            </p>

                                                            <p>
                                                              <strong>
                                                                Email:
                                                              </strong>{" "}
                                                              {test
                                                                .OtherMedicalUser
                                                                ?.email ||
                                                                "N/A"}
                                                            </p>

                                                            <p>
                                                              <strong>
                                                                Phone:
                                                              </strong>{" "}
                                                              {test
                                                                .OtherMedicalUser
                                                                ?.phone ||
                                                                "N/A"}
                                                            </p>
                                                          </div>

                                                          {/* LOCATION */}

                                                          {(test
                                                            .OtherMedicalUser
                                                            ?.address ||
                                                            test
                                                              .OtherMedicalUser
                                                              ?.city ||
                                                            test
                                                              .OtherMedicalUser
                                                              ?.area ||
                                                            test
                                                              .OtherMedicalUser
                                                              ?.state ||
                                                            test
                                                              .OtherMedicalUser
                                                              ?.pincode) && (
                                                            <div className="mt-2.5">
                                                              <p className="flex items-start gap-2 text-xs text-gray-700">
                                                                <FaMapMarkerAlt className="text-red-500 mt-0.5" />

                                                                <span>
                                                                  {test
                                                                    .OtherMedicalUser
                                                                    ?.address && (
                                                                    <span className="block">
                                                                      {
                                                                        test
                                                                          .OtherMedicalUser
                                                                          .address
                                                                      }
                                                                    </span>
                                                                  )}

                                                                  <span>
                                                                    {test
                                                                      .OtherMedicalUser
                                                                      ?.area && (
                                                                      <span>
                                                                        {
                                                                          test
                                                                            .OtherMedicalUser
                                                                            .area
                                                                        }
                                                                      </span>
                                                                    )}

                                                                    {test
                                                                      .OtherMedicalUser
                                                                      ?.area &&
                                                                      test
                                                                        .OtherMedicalUser
                                                                        ?.city && (
                                                                        <span>
                                                                          ,{" "}
                                                                        </span>
                                                                      )}

                                                                    {test
                                                                      .OtherMedicalUser
                                                                      ?.city && (
                                                                      <span>
                                                                        {
                                                                          test
                                                                            .OtherMedicalUser
                                                                            .city
                                                                        }
                                                                      </span>
                                                                    )}

                                                                    {test
                                                                      .OtherMedicalUser
                                                                      ?.city &&
                                                                      test
                                                                        .OtherMedicalUser
                                                                        ?.state && (
                                                                        <span>
                                                                          ,{" "}
                                                                        </span>
                                                                      )}

                                                                    {test
                                                                      .OtherMedicalUser
                                                                      ?.state && (
                                                                      <span>
                                                                        {
                                                                          test
                                                                            .OtherMedicalUser
                                                                            .state
                                                                        }
                                                                      </span>
                                                                    )}

                                                                    {test
                                                                      .OtherMedicalUser
                                                                      ?.pincode && (
                                                                      <span>
                                                                        {" - "}
                                                                        {
                                                                          test
                                                                            .OtherMedicalUser
                                                                            .pincode
                                                                        }
                                                                      </span>
                                                                    )}
                                                                  </span>
                                                                </span>
                                                              </p>
                                                            </div>
                                                          )}

                                                          {/* MAP */}

                                                          {test.OtherMedicalUser
                                                            ?.googleMapLink && (
                                                            <a
                                                              href={
                                                                test
                                                                  .OtherMedicalUser
                                                                  .googleMapLink
                                                              }
                                                              target="_blank"
                                                              rel="noopener noreferrer"
                                                              onClick={(
                                                                event,
                                                              ) =>
                                                                event.stopPropagation()
                                                              }
                                                              className="inline-flex items-center gap-1.5 mt-2 text-[#0ea5e9] font-semibold text-xs hover:underline"
                                                            >
                                                              <FaMapMarkerAlt />
                                                              View Location
                                                              <FaExternalLinkAlt className="text-[8px]" />
                                                            </a>
                                                          )}

                                                          {/* LAB DESCRIPTION */}

                                                          {test?.labDescription && (
                                                            <div className="mt-2.5 rounded-lg bg-white border border-emerald-200 p-2.5">
                                                              <p className="text-[9px] font-bold text-emerald-700 uppercase mb-1">
                                                                Lab Description
                                                              </p>

                                                              <p className="text-xs text-gray-700 leading-5">
                                                                {
                                                                  test.labDescription
                                                                }
                                                              </p>
                                                            </div>
                                                          )}
                                                        </div>
                                                      )}

                                                      {/* LAB REPORTS */}

                                                      {Array.isArray(
                                                        test?.labFiles,
                                                      ) &&
                                                        test.labFiles.length >
                                                          0 && (
                                                          <div className="mt-2.5">
                                                            <div className="flex items-center gap-2 mb-1.5">
                                                              <FaFileMedical className="text-[#1a3a5c] text-xs" />

                                                              <h5 className="text-xs font-bold text-gray-800">
                                                                Lab Reports
                                                              </h5>
                                                            </div>

                                                            <div className="flex flex-wrap gap-1.5">
                                                              {test.labFiles.map(
                                                                (file, i) => {
                                                                  const url =
                                                                    getFileUrl(
                                                                      file,
                                                                    );

                                                                  if (!url) {
                                                                    return null;
                                                                  }

                                                                  const fileName =
                                                                    url
                                                                      .split(
                                                                        "/",
                                                                      )
                                                                      .pop()
                                                                      ?.split(
                                                                        "?",
                                                                      )[0] ||
                                                                    `Report ${
                                                                      i + 1
                                                                    }`;

                                                                  return (
                                                                    <a
                                                                      key={
                                                                        file?.id ||
                                                                        file?._id ||
                                                                        i
                                                                      }
                                                                      href={url}
                                                                      target="_blank"
                                                                      rel="noopener noreferrer"
                                                                      onClick={(
                                                                        event,
                                                                      ) =>
                                                                        event.stopPropagation()
                                                                      }
                                                                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-white text-[10px] font-bold shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
                                                                      style={{
                                                                        backgroundImage:
                                                                          "linear-gradient(135deg, rgb(26,58,92), rgb(14,165,233))",
                                                                      }}
                                                                    >
                                                                      <FaFileMedical />

                                                                      <span className="max-w-[180px] truncate">
                                                                        {
                                                                          fileName
                                                                        }
                                                                      </span>
                                                                    </a>
                                                                  );
                                                                },
                                                              )}
                                                            </div>
                                                          </div>
                                                        )}
                                                    </div>
                                                  );
                                                },
                                              )}
                                            </div>
                                          </div>
                                        )}
                                    </div>
                                  )}
                                </article>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  </section>
                );
              })}
          </div>
        )}
      </main>

      {/* ======================================================
          RESCHEDULE MODAL
      ====================================================== */}

      <RescheduleModal
        isOpen={isRescheduleModalOpen}
        onClose={() => {
          setIsRescheduleModalOpen(false);

          setSelectedAppointment(null);
        }}
        onSubmit={({ date, time }) => handleRescheduleSubmit(date, time)}
        selectedAppointment={selectedAppointment}
        actionLoading={actionLoading}
        newDate={newDate}
        newTime={newTime}
      />
    </div>
  );
};

export default UserDashboard;
